// Node.js canvas 타입 정의 (의존성 순서대로)
interface NodeImage {
  width: number;
  height: number;
}

interface NodeCanvasRenderingContext2D {
  drawImage(image: NodeImage, dx: number, dy: number): void;
}

interface NodeCanvas {
  width: number;
  height: number;
  getContext(contextId: '2d'): NodeCanvasRenderingContext2D;
  toBuffer(type?: string): Buffer;
}

interface NodeCanvasModule {
  createCanvas: (width: number, height: number) => NodeCanvas;
  loadImage: (url: string) => Promise<NodeImage>;
}

export class ImageUtil {
  /**
   * Calculate optimal margin size for Gaussian blur to prevent edge clipping.
   * Accounts for threshold cutoff: blur expansion is limited by alpha threshold.
   *
   * Gaussian function: G(x) = exp(-x²/2σ²) × 255
   * At distance x from center, alpha value = G(x)
   * Actual expansion = distance where G(x) = alpha_threshold
   *
   * Solving: exp(-x²/2σ²) × 255 = threshold
   * → x = σ × sqrt(-2 × ln(threshold/255))
   *
   * @param blurSigma - Gaussian blur sigma value
   * @param alphaThreshold - Alpha threshold (0-255). Defaults to 1 for maximum expansion
   * @returns Margin size in pixels (0 if blurSigma <= 0)
   */
  static calculateBlurMargin(blurSigma: number, alphaThreshold = 1): number {
    if (blurSigma <= 0) return 0;

    // Normalize threshold to 0-1 range
    const normalizedThreshold = Math.max(1, Math.min(255, alphaThreshold)) / 255;

    // Calculate distance where Gaussian falls to threshold
    // G(x) = exp(-x²/2σ²) = normalizedThreshold
    // x = σ × sqrt(-2 × ln(normalizedThreshold))
    const effectiveRadius = blurSigma * Math.sqrt(-2 * Math.log(normalizedThreshold));

    return Math.ceil(effectiveRadius);
  }

  /**
   * Get file size from image URL using HEAD request
   * Supports URLs, base64 data URIs, and local file paths (Node.js only)
   * @param imageUrl - URL of the image to check, local file path, or base64 data URI
   * @returns File size in bytes, or null if not available
   */
  static async getImageFileSize(imageUrl: string): Promise<number | null> {
    try {
      // Handle base64 data URI
      if (imageUrl.startsWith('data:')) {
        const base64Match = imageUrl.match(/^data:[^;]+;base64,(.+)$/);
        if (base64Match && base64Match[1]) {
          const base64Data = base64Match[1];
          // Calculate size: each base64 character represents 6 bits
          // Remove padding characters and calculate actual byte size
          const paddingCount = (base64Data.match(/=/g) || []).length;
          const base64Length = base64Data.length;
          const byteSize = Math.floor((base64Length * 3) / 4) - paddingCount;
          return byteSize;
        }
        return null;
      }

      // Check if it's a local file path (Node.js only)
      if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
        if (this.isNodeJs()) {
          try {
            // Check if we're in Jest test environment
            const isJestEnvironment =
              typeof process !== 'undefined' &&
              (process.env.NODE_ENV === 'test' || process.env.JEST_WORKER_ID !== undefined);

            if (isJestEnvironment) {
              // In Jest environment, fs operations are not critical for testing
              // Return null silently to avoid console noise
              return null;
            }

            // Use global require to avoid webpack static analysis
            const fs = (global as unknown as { require: NodeRequire }).require('fs/promises');
            const stats = await fs.stat(imageUrl);
            return stats.size;
          } catch (fsError) {
            // If fs operation fails, it might not be a valid local path
            console.warn('Failed to access local file:', fsError);
            return null;
          }
        } else {
          // Browser environment - cannot access local file paths
          console.warn('Local file paths are not supported in browser environment');
          return null;
        }
      }

      // Handle HTTP/HTTPS URL using fetch HEAD request
      const response = await fetch(imageUrl, { method: 'HEAD' });

      if (!response.ok) {
        throw new Error(`Failed to fetch image info: ${response.status} ${response.statusText}`);
      }

      const contentLength = response.headers.get('content-length');

      if (!contentLength) {
        return null;
      }

      const fileSize = parseInt(contentLength, 10);
      return isNaN(fileSize) ? null : fileSize;
    } catch (error) {
      console.warn('Failed to get image file size:', error);
      return null;
    }
  }

  /**
   * 현재 실행 환경이 Node.js인지 확인합니다.
   * @returns boolean - Node.js 환경 여부
   */
  private static isNodeJs(): boolean {
    return (
      typeof window === 'undefined' &&
      typeof global !== 'undefined' &&
      typeof process !== 'undefined' &&
      !!process.versions?.node
    );
  }

  /**
   * URL에 캐시 버스터를 추가합니다.
   * SVG나 이미지 캐싱 이슈를 방지하기 위해 필요합니다.
   * @param url - 원본 URL
   * @returns string - 캐시 버스터가 추가된 URL
   */
  private static addCacheBuster(url: string): string {
    // data URI와 blob URL은 캐시 버스터가 불필요하고 오히려 문제를 일으킴
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;

    // 캐시 버스터는 브라우저에서 CORS 문제와 캐싱 이슈를 해결하는 데 필요
    // 특히 S3와 같은 CDN에서 동일한 URL에 대한 CORS 정책이 다를 수 있음
    return url.includes('?') ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`;
  }

  /**
   * Node.js 환경에서 이미지를 로드합니다.
   * @param url - 이미지 URL
   * @returns Promise<NodeImage> - 로드된 이미지 객체
   */
  private static async loadImageInNode(url: string): Promise<NodeImage> {
    try {
      const canvas = (await import('canvas')) as { default: NodeCanvasModule };
      const loadImage = canvas.default?.loadImage;

      if (!loadImage) {
        throw new Error('Failed to load canvas.loadImage');
      }

      return await loadImage(url);
    } catch {
      throw new Error('canvas package is required for Node.js environment');
    }
  }

  /**
   * 브라우저 환경에서 이미지를 로드합니다. CORS 설정을 포함합니다.
   * @param url - 이미지 URL
   * @returns Promise<HTMLImageElement> - 로드된 이미지 엘리먼트
   */
  private static async loadImageInBrowser(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');

      // CORS 우회 설정
      img.crossOrigin = 'Anonymous';

      img.onload = async () => {
        try {
          await img.decode();
          await this.rafFrame(3); // 3 프레임 대기 후 안전하게 resolve
          resolve(img);
        } catch (e) {
          reject(new Error(`Failed to decode image: ${url}`));
        }
      };
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));

      img.src = url;
    });
  }

  private static rafFrame(times = 1): Promise<void> {
    return new Promise((resolve) => {
      function next(count: number) {
        if (count <= 0) {
          resolve();
        } else {
          requestAnimationFrame(() => next(count - 1));
        }
      }
      next(times);
    });
  }

  /**
   * Node.js 환경에서 Canvas를 생성합니다.
   * @param width - Canvas 너비
   * @param height - Canvas 높이
   * @returns Promise<NodeCanvas> - Node.js Canvas 객체
   */
  private static async createCanvasInNode(width: number, height: number): Promise<NodeCanvas> {
    try {
      const canvas = (await import('canvas')) as { default: NodeCanvasModule };
      const createCanvas = canvas.default?.createCanvas;

      if (!createCanvas) {
        throw new Error('Failed to load canvas.createCanvas');
      }

      return createCanvas(width, height);
    } catch {
      throw new Error('canvas package is required for Node.js environment');
    }
  }

  /**
   * 브라우저 환경에서 Canvas를 생성합니다.
   * @param width - Canvas 너비
   * @param height - Canvas 높이
   * @returns HTMLCanvasElement - 생성된 Canvas 엘리먼트
   */
  private static createCanvasInBrowser(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  /**
   * 이미지 URL로부터 Canvas를 생성합니다.
   * @param url - 이미지 URL
   * @returns Promise<HTMLCanvasElement | NodeCanvas> - 생성된 Canvas
   */
  static async makeCanvasByUrl(url: string): Promise<HTMLCanvasElement | NodeCanvas> {
    const urlWithCacheBuster = this.addCacheBuster(url);

    if (this.isNodeJs()) {
      // Node.js 환경
      const img = await this.loadImageInNode(urlWithCacheBuster);
      const canvas = await this.createCanvasInNode(img.width, img.height);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      return canvas;
    } else {
      // 브라우저 환경
      const img = await this.loadImageInBrowser(urlWithCacheBuster);
      const canvas = this.createCanvasInBrowser(img.width, img.height);
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      ctx.drawImage(img, 0, 0);
      return canvas;
    }
  }

  /**
   * Canvas를 data URI로 변환합니다.
   * @param canvas - 변환할 Canvas
   * @returns string - base64 data URI
   */
  private static canvasToDataUri(canvas: HTMLCanvasElement | NodeCanvas): string {
    if (this.isNodeJs()) {
      // Node.js 환경
      const nodeCanvas = canvas as NodeCanvas;
      const buffer = nodeCanvas.toBuffer('image/png');
      return `data:image/png;base64,${buffer.toString('base64')}`;
    } else {
      // 브라우저 환경
      const htmlCanvas = canvas as HTMLCanvasElement;
      return htmlCanvas.toDataURL('image/png');
    }
  }

  /**
   * URL을 base64 data URI로 변환합니다. CORS 문제를 해결하기 위해 Image 객체를 사용합니다.
   * @param url - 변환할 이미지 URL
   * @returns Promise<string> - base64 data URI
   */
  static async urlToDataUri(url: string): Promise<string> {
    if (url.startsWith('data:')) return url;

    const canvas = await this.makeCanvasByUrl(url);
    return this.canvasToDataUri(canvas);
  }

  /**
   * 이미지 URL로부터 이미지의 실제 크기(width, height)를 얻습니다.
   * @param url - 이미지 URL
   * @returns Promise<{width: number, height: number}> - 이미지의 실제 크기
   */
  static async getImageDimensions(url: string): Promise<{ width: number; height: number }> {
    const urlWithCacheBuster = this.addCacheBuster(url);

    if (this.isNodeJs()) {
      // Node.js 환경
      const img = await this.loadImageInNode(urlWithCacheBuster);
      return { width: img.width, height: img.height };
    } else {
      // 브라우저 환경
      const img = await this.loadImageInBrowser(urlWithCacheBuster);
      return { width: img.width, height: img.height };
    }
  }
}
